import { useCallback, useEffect, useRef } from "react";
import { useAppState } from "./AppContext";
import { ListRowPlaceholder } from "./components/ListRow";
import OverlayLayerItem from "./OverlayLayerItem";
import { useEsri } from "./EsriContext";
import { queryPopupContain } from "./popup";
import { strActiveOverlays } from "./utils";
import { DEFAULT_POPUP_TEMPLATE_ITEMS } from "./settings";
import { useSearch } from "./esri-components/SearchProvider";

const markerPointSymbol = {
  type: "simple-marker",
  size: "8px",
  color: [0, 0, 0],
};
const markerPointId = "point_marker";
const highlightRegionId = "highlight_region";

const OverlayLayer = () => {
  const [appState, setAppState] = useAppState();
  const { service_list, active_overlay } = appState;
  const { esriLoader, geoProcessor } = useEsri();
  const activeOverlayRef = useRef();
  activeOverlayRef.current = active_overlay;
  const { getMapSearchRef } = useSearch();

  const removeMarkerPoint = useCallback(() => {
    if (!esriLoader) return;
    if (getMapSearchRef()) getMapSearchRef().clear(); // clear map search point
    const getPreviousMarkerPoint = esriLoader.view.graphics.items.filter(
      function (graphic) {
        return graphic.id === markerPointId;
      }
    )[0];
    esriLoader.view.graphics.remove(getPreviousMarkerPoint);
  }, [esriLoader, getMapSearchRef]);

  const removeHighlightedAreas = useCallback(() => {
    if (!esriLoader) return;
    const getPreviousHightlight = esriLoader.view.graphics.items.filter(
      function (graphic) {
        return graphic.id === highlightRegionId;
      }
    )[0];
    esriLoader.view.graphics.remove(getPreviousHightlight);
  }, [esriLoader]);

  useEffect(() => {
    if (!esriLoader) return;

    const spinner = new esriLoader.Spinner({
      view: esriLoader.view,
    });
    esriLoader.view.ui.add(spinner);

    esriLoader.view.on("click", async (viewEvent) => {
      removeMarkerPoint();
      removeHighlightedAreas();
      const hitTestResponse = await esriLoader.view.hitTest(viewEvent);
      
      const newMarkerPointGraphic = new esriLoader.Graphic(
        viewEvent["mapPoint"],
        markerPointSymbol
      );
      newMarkerPointGraphic.id = markerPointId;
      esriLoader.view.graphics.add(newMarkerPointGraphic);

      // this will get nearest target graphic from the map canvas.
      const targetGraphic = hitTestResponse["results"].filter((result) => {
        return result["graphic"]["layer"];
      })?.[0]?.["graphic"];

      if (!targetGraphic) return;
      targetGraphic.popupTemplate = {
        ...DEFAULT_POPUP_TEMPLATE_ITEMS,
        content: function () {
          return queryPopupContain(
            viewEvent["mapPoint"],
            strActiveOverlays(activeOverlayRef.current),
            spinner
          );
        },
      };

      esriLoader.view.popup.open({
        location: viewEvent["mapPoint"],
        features: [targetGraphic],
      });
    });

    esriLoader.view.popup.watch("visible", (popUpStatusChange) => {
      if (popUpStatusChange) return;
      removeMarkerPoint();
      spinner.hide();
    });

    return () => {};
  }, [esriLoader, geoProcessor, removeMarkerPoint, removeHighlightedAreas]);

  const onOverlayChange = ({ alias, layerState }) => {
    setAppState((prevState) => ({
      ...prevState,
      active_overlay: {
        ...prevState.active_overlay,
        [alias]: { status: layerState },
      },
    }));
  };

  return (
    <>
      {!service_list ? (
        Array(3)
          .fill("")
          .map((_, index) => (
            <ListRowPlaceholder key={`placeholder_${index}`} />
          ))
      ) : (
        <div className="mb-8">
          {Object.keys(service_list).map((key) => (
            <OverlayLayerItem
              layer={service_list[key]}
              key={service_list[key]["id"]}
              onLayerCheck={onOverlayChange}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default OverlayLayer;
